/**
 * useProcessCanvas — Manages React Flow canvas state for the live process canvas.
 *
 * Responsibilities:
 * - Holds nodes[] + edges[] state (via React Flow useNodesState/useEdgesState)
 * - Loads initial canvas state + preloaded entities on mount
 * - applyDelta(): merges new/updated nodes/edges into state
 * - Always applies dagre layout with variable node sizes
 * - Exposes layoutVersion for fitView triggering
 * - Maps legacy 12-type nodes to 5 display types
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useNodesState,
  useEdgesState,
  Position,
  type Node,
  type Edge,
  type OnNodesChange,
  type OnEdgesChange,
} from '@xyflow/react';
import dagre from 'dagre';
import type {
  CanvasDelta,
  CanvasNodeUpdate,
  CanvasDisplayType,
  CanvasCluster,
  DismissedCanvasNode,
} from '@shared/types.ts';
import { mapToDisplayType, deriveSubType, mapEdgeType } from '@shared/canvas-type-map.ts';
import { inferCanonicalType } from '@/lib/edgeLabels';
import {
  getCanvasContext,
  listNodeContexts,
  listCanvasEvents,
  type NodeContextRow,
  type CanvasEventRow,
} from '@/services/api';

// ── Constants ──────────────────────────────────────────────────────────────

/**
 * Node dimensions by display type for dagre layout.
 *
 * IMPORTANT: keep these in sync with the actual rendered card size.
 * Phase 4.1 unified every node card to a fixed 232px width — feeding dagre
 * the old per-type widths produced uneven gaps and edge overlap, especially
 * once new nodes started arriving on top of the existing layout.
 *
 * Heights vary slightly by content density:
 *   - TRIGGER / ACTOR / TOOL: short body, no chips → ~120px typical
 *   - STEP: bigger label + multi-line description + connections footer → ~170px
 *   - INSIGHT: subType badge + description → ~140px
 *
 * Slightly over-estimating height is safer than under: dagre uses these
 * to space ranks, so a too-small value packs nodes into each other.
 */
const NODE_DIMENSIONS: Record<CanvasDisplayType, { width: number; height: number }> = {
  STEP:    { width: 232, height: 170 },
  ACTOR:   { width: 232, height: 120 },
  TOOL:    { width: 232, height: 120 },
  INSIGHT: { width: 232, height: 150 },
  TRIGGER: { width: 232, height: 120 },
};

const DEFAULT_DIMENSIONS = { width: 232, height: 140 };

/** Map display types to React Flow node type identifiers */
const NODE_TYPE_MAP: Record<string, string> = {
  STEP: 'step',
  ACTOR: 'actor',
  TOOL: 'tool',
  INSIGHT: 'insight',
  TRIGGER: 'trigger',
  // Legacy fallbacks (map old types to new components)
  TASK: 'step',
  PROCESS: 'step',
  PERSON: 'actor',
  ROLE: 'actor',
  SYSTEM: 'tool',
  DOCUMENT: 'tool',
  RULE: 'insight',
  DECISION: 'insight',
  KPI: 'insight',
  RISK: 'insight',
  WORKAROUND: 'insight',
  BUSINESS_OBJECT: 'insight',
  EVENT: 'trigger',
};

// ── Types ──────────────────────────────────────────────────────────────────

export interface CanvasNodeData extends Record<string, unknown> {
  label: string;
  description?: string;
  sourceType: 'new' | 'existing' | 'conflicting' | 'ambiguous';
  canvasNodeType: string;
  /** Original granular type (e.g., "RISK", "SYSTEM", "DOCUMENT") */
  subType?: string;
  /** Interview section for grouping */
  section?: string;
  /** Step number for STEP nodes */
  stepNumber?: number;
  edgeCount: number;
  conflictNew?: string;
  conflictExisting?: string;
  sourceName?: string;
  sourceDate?: string;
  /** Whether this node is staged (provisional, pending confirmation) */
  staged: boolean;
  /** User-added context notes */
  additionalContext?: Array<{ text: string; timestamp: string; kind?: 'detail' | 'exception' | 'correction' }>;
}

/** A canvas node is a React Flow Node typed with CanvasNodeData */
export type CanvasFlowNode = Node<CanvasNodeData>;

export interface UndoAction {
  /** Stable id used by the UndoPill to reset its 8s progress bar. */
  id: string;
  /** Short human label, e.g. `Added "Tier-2 ticket"` or `Skipped 3 proposals`. */
  label: string;
  /** Restore callback — pure side-effect that puts canvas state back. */
  restore: () => void;
}

export interface UseProcessCanvasReturn {
  nodes: CanvasFlowNode[];
  edges: Edge[];
  onNodesChange: OnNodesChange<CanvasFlowNode>;
  onEdgesChange: OnEdgesChange;
  applyDelta: (delta: CanvasDelta) => void;
  confirmNode: (nodeId: string) => void;
  dismissNode: (nodeId: string) => void;
  /** Confirm every currently-staged node in one batch. */
  acceptAllStaged: () => void;
  /** Dismiss every currently-staged node in one batch. */
  skipAllStaged: () => void;
  /** Append a typed note to a node. `kind` carries the AddContextSheet category. */
  addNodeContext: (nodeId: string, text: string, kind?: 'detail' | 'exception' | 'correction') => void;
  /** Most recent reversible canvas action — null when nothing to undo. */
  undoAction: UndoAction | null;
  /** Roll back the most recent canvas action. */
  undoLast: () => void;
  /** Re-run dagre layout on the current node + edge set; bumps layoutVersion. */
  relayout: () => void;
  isLoadingCanvas: boolean;
  canvasError: string | null;
  layoutVersion: number;
  /** Initial clusters loaded from persisted state (for useClusterManager init) */
  initialClusters: CanvasCluster[];
  /** Nodes dismissed by the user (persisted for coordinator awareness) */
  dismissedNodes: DismissedCanvasNode[];
  /**
   * Track C.3 — true while the timeline scrubber has rewound the canvas to a
   * prior turn. Destructive actions (accept/dismiss/add-context) should be
   * disabled while replaying.
   */
  isReplaying: boolean;
  /** Rebuild canvas state by replaying every CanvasEvent with turnIndex <= idx. */
  replayToIndex: (idx: number) => void;
  /** Apply remaining events and return to live state. */
  replayToLatest: () => void;
}

// ── Dagre layout helper ────────────────────────────────────────────────────

function applyDagreLayout(
  nodes: CanvasFlowNode[],
  edges: Edge[],
): CanvasFlowNode[] {
  if (nodes.length === 0) return nodes;

  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  // LR (left → right time flow) with generous ranksep so the orthogonal
  // elbow edges from Phase 4.2 + their label pills have room to route
  // without crashing into the next column. `align: 'UL'` keeps cards in
  // each rank top-aligned for a steady horizontal baseline. `marginx/y`
  // gives the bounding box a little outer padding so the auto-fit zoom
  // doesn't crop the leftmost/topmost cards.
  g.setGraph({
    rankdir: 'LR',
    align: 'UL',
    nodesep: 100,
    ranksep: 200,
    edgesep: 30,
    marginx: 40,
    marginy: 40,
    ranker: 'tight-tree',
  });

  for (const node of nodes) {
    const displayType = mapToDisplayType(node.data.canvasNodeType);
    const dims = NODE_DIMENSIONS[displayType] ?? DEFAULT_DIMENSIONS;
    g.setNode(node.id, { width: dims.width, height: dims.height });
  }

  for (const edge of edges) {
    g.setEdge(edge.source, edge.target);
  }

  dagre.layout(g);

  return nodes.map((node) => {
    const pos = g.node(node.id);
    const displayType = mapToDisplayType(node.data.canvasNodeType);
    const dims = NODE_DIMENSIONS[displayType] ?? DEFAULT_DIMENSIONS;
    return {
      ...node,
      position: {
        x: pos.x - dims.width / 2,
        y: pos.y - dims.height / 2,
      },
    };
  });
}

// ── Edge anchor picker ─────────────────────────────────────────────────────

/**
 * Pick a handle pair for an edge based on the relative positions of source
 * and target node centers. BaseNode exposes only two source/target handle
 * pairs: Right→Left (default) and Bottom→Top (id 'b' / 't'). When the target
 * sits clearly *below* the source, vertical routing avoids the long Right→Left
 * detour that drags the orthogonal elbow path through other cards.
 */
interface EdgeAnchors {
  sourceHandle?: string;
  targetHandle?: string;
  sourcePosition: Position;
  targetPosition: Position;
}

function pickEdgeAnchors(
  src: { pos: { x: number; y: number }; dims: { width: number; height: number } },
  tgt: { pos: { x: number; y: number }; dims: { width: number; height: number } },
): EdgeAnchors {
  const srcCx = src.pos.x + src.dims.width / 2;
  const srcCy = src.pos.y + src.dims.height / 2;
  const tgtCx = tgt.pos.x + tgt.dims.width / 2;
  const tgtCy = tgt.pos.y + tgt.dims.height / 2;
  const dx = tgtCx - srcCx;
  const dy = tgtCy - srcCy;

  if (dy > 0 && Math.abs(dy) > Math.abs(dx)) {
    return {
      sourceHandle: 'b',
      targetHandle: 't',
      sourcePosition: Position.Bottom,
      targetPosition: Position.Top,
    };
  }
  return {
    sourcePosition: Position.Right,
    targetPosition: Position.Left,
  };
}

/**
 * Refresh every edge's handle pair against the current node positions.
 * Call after any layout pass — without this, edges keep stale handle
 * assignments from when they were first constructed and React Flow
 * defaults every edge to Right→Left.
 */
function applyEdgeAnchors(edges: Edge[], nodes: CanvasFlowNode[]): Edge[] {
  const lookup = new Map<string, { pos: { x: number; y: number }; dims: { width: number; height: number } }>();
  for (const node of nodes) {
    const dims = NODE_DIMENSIONS[mapToDisplayType(node.data.canvasNodeType)] ?? DEFAULT_DIMENSIONS;
    lookup.set(node.id, { pos: node.position, dims });
  }
  return edges.map((e) => {
    const src = lookup.get(e.source);
    const tgt = lookup.get(e.target);
    if (!src || !tgt) return e;
    return { ...e, ...pickEdgeAnchors(src, tgt) };
  });
}

// ── Edge style helper ──────────────────────────────────────────────────────

function edgeLabel(type: string): string {
  const labels: Record<string, string> = {
    NEXT_STEP: 'next',
    USES: 'uses',
    RESPONSIBLE_FOR: 'responsible',
    GOVERNED_BY: 'governed by',
    TRIGGERS: 'triggers',
    MEASURED_BY: 'measured by',
    MITIGATES: 'mitigates',
    RELATES_TO: 'relates to',
    // Legacy labels
    REFERENCES: 'uses',
    APPROVES: 'governed by',
    FILLS_ROLE: 'responsible',
    PROCESSES: 'relates to',
    WORKS_AROUND: 'mitigates',
  };
  return labels[type] ?? type;
}

// ── Hook ───────────────────────────────────────────────────────────────────

export function useProcessCanvas(sessionId: string): UseProcessCanvasReturn {
  const [nodes, setNodes, onNodesChange] = useNodesState<CanvasFlowNode>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [isLoadingCanvas, setIsLoadingCanvas] = useState(true);
  const [canvasError, setCanvasError] = useState<string | null>(null);
  const [layoutVersion, setLayoutVersion] = useState(0);
  const [initialClusters, setInitialClusters] = useState<CanvasCluster[]>([]);
  const [dismissedNodes, setDismissedNodes] = useState<DismissedCanvasNode[]>([]);

  // Track C.3 — event-sourced replay state. `eventsRef` caches the full
  // CanvasEvent log fetched on session load; `isReplaying` flips true while
  // the scrubber has rewound the canvas to a prior turn (suppresses undo
  // pushes and is surfaced to the UI to disable destructive actions).
  const eventsRef = useRef<CanvasEventRow[]>([]);
  const isReplayingRef = useRef(false);
  const [isReplaying, setIsReplaying] = useState(false);

  // Keep mutable refs to nodes + edges for snapshot reads in undo / batch ops
  const nodesRef = useRef<CanvasFlowNode[]>([]);
  const edgesRef = useRef<Edge[]>([]);
  useEffect(() => {
    nodesRef.current = nodes;
  }, [nodes]);
  useEffect(() => {
    edgesRef.current = edges;
  }, [edges]);

  // ── Undo (Phase 6.1) ───────────────────────────────────────────────────────
  // Single-slot, 8s auto-clear. Each canvas action captures the prior state +
  // pushes an UndoAction. Hitting Undo restores that snapshot and clears the
  // slot. Matches design/kaiva-canvas/project/components/app.jsx behaviour.

  const [undoAction, setUndoAction] = useState<UndoAction | null>(null);
  const undoTimerRef = useRef<number | null>(null);

  const clearUndoTimer = useCallback(() => {
    if (undoTimerRef.current !== null) {
      window.clearTimeout(undoTimerRef.current);
      undoTimerRef.current = null;
    }
  }, []);

  const pushUndo = useCallback(
    (action: UndoAction) => {
      // Track C.3 — never push undo entries while replaying. The deltas
      // applied during replay aren't real user actions.
      if (isReplayingRef.current) return;
      clearUndoTimer();
      setUndoAction(action);
      undoTimerRef.current = window.setTimeout(() => setUndoAction(null), 8000);
    },
    [clearUndoTimer],
  );

  const undoLast = useCallback(() => {
    setUndoAction((current) => {
      if (current) current.restore();
      return null;
    });
    clearUndoTimer();
  }, [clearUndoTimer]);

  // Tear down any pending timer when the hook unmounts
  useEffect(() => () => clearUndoTimer(), [clearUndoTimer]);

  // ── Count edges per node ─────────────────────────────────────────────────

  const updateEdgeCounts = useCallback(
    (currentNodes: CanvasFlowNode[], currentEdges: Edge[]): CanvasFlowNode[] => {
      const counts = new Map<string, number>();
      for (const edge of currentEdges) {
        counts.set(edge.source, (counts.get(edge.source) ?? 0) + 1);
        counts.set(edge.target, (counts.get(edge.target) ?? 0) + 1);
      }
      return currentNodes.map((node) => ({
        ...node,
        data: {
          ...node.data,
          edgeCount: counts.get(node.id) ?? 0,
        },
      }));
    },
    [],
  );

  // ── Convert a CanvasNodeUpdate to a React Flow Node ──────────────────────

  const toReactFlowNode = useCallback(
    (update: CanvasNodeUpdate, index: number, staged = false): CanvasFlowNode => {
      const displayType = mapToDisplayType(update.type);
      const subType = update.subType ?? deriveSubType(update.type);

      return {
        id: update.id,
        type: NODE_TYPE_MAP[displayType] ?? 'step',
        position: update.position ?? { x: index * 220, y: 0 },
        data: {
          label: update.name,
          description: update.description,
          sourceType: update.sourceType,
          canvasNodeType: displayType,
          subType,
          section: update.section,
          stepNumber: update.stepNumber,
          edgeCount: 0,
          conflictNew: update.conflictNew,
          conflictExisting: update.conflictExisting,
          sourceName: update.sourceName,
          sourceDate: update.sourceDate,
          staged,
        },
      };
    },
    [],
  );

  // ── Load initial canvas state on mount ───────────────────────────────────

  useEffect(() => {
    let cancelled = false;

    async function loadCanvas() {
      setIsLoadingCanvas(true);
      setCanvasError(null);

      try {
        // Track C.2 — fetch node-context rows alongside the canvas snapshot.
        // The dedicated table is the source of truth post-Track C; the
        // canvasState `additionalContext[]` is kept as a fallback so legacy
        // sessions without migrated rows still render their badges.
        // Track C.3 — also fetch the canvas-events log so the timeline
        // scrubber can rebuild prior turns. Sessions started before C.3 ship
        // with no rows; replay degrades to a no-op for them.
        const [response, nodeContextRows, canvasEventRows] = await Promise.all([
          getCanvasContext(sessionId),
          listNodeContexts(sessionId).catch((err) => {
            console.warn('[ProcessCanvas] listNodeContexts failed, falling back to canvasState:', err);
            return [] as NodeContextRow[];
          }),
          listCanvasEvents(sessionId).catch((err) => {
            console.warn('[ProcessCanvas] listCanvasEvents failed, replay disabled:', err);
            return [] as CanvasEventRow[];
          }),
        ]);
        if (cancelled) return;

        eventsRef.current = canvasEventRows;

        const contextsByNodeId = new Map<string, NodeContextRow[]>();
        for (const row of nodeContextRows) {
          const list = contextsByNodeId.get(row.nodeId) ?? [];
          list.push(row);
          contextsByNodeId.set(row.nodeId, list);
        }

        let initialNodes: CanvasFlowNode[] = [];
        let initialEdges: Edge[] = [];

        // Restore persisted canvas state (with backward-compat type mapping)
        if (response.canvasState) {
          initialNodes = response.canvasState.nodes.map((n, i): CanvasFlowNode => {
            const displayType = mapToDisplayType(n.type);
            const subType = n.subType ?? deriveSubType(n.type);

            // Prefer server-side NodeContext rows when present; fall back to
            // the canvasState snapshot for unmigrated sessions.
            const serverContexts = contextsByNodeId.get(n.id);
            const additionalContext = serverContexts && serverContexts.length > 0
              ? serverContexts.map((row) => ({
                  text: row.text,
                  timestamp: row.createdAt,
                  kind: row.kind,
                }))
              : n.additionalContext;

            return {
              id: n.id,
              type: NODE_TYPE_MAP[displayType] ?? 'step',
              position: n.position ?? { x: i * 220, y: 0 },
              data: {
                label: n.name,
                description: n.description,
                sourceType: n.sourceType,
                canvasNodeType: displayType,
                subType,
                section: n.section,
                stepNumber: n.stepNumber,
                edgeCount: n.edgeCount ?? 0,
                conflictNew: n.conflictNew,
                conflictExisting: n.conflictExisting,
                sourceName: n.sourceName,
                sourceDate: n.sourceDate,
                staged: n.staged ?? false,
                additionalContext,
              },
            };
          });

          initialEdges = response.canvasState.edges.map((e): Edge => {
            // Pre-D.1 saves wrote rendered labels ('next', 'uses', …) as the
            // type. mapEdgeType silently coerces those to RELATES_TO, so
            // when it returns RELATES_TO for a non-RELATES_TO input we treat
            // the persisted string as a label and recover the canonical type.
            const mapped = mapEdgeType(e.type);
            const canonicalType = mapped === 'RELATES_TO' && e.type !== 'RELATES_TO'
              ? inferCanonicalType(e.type)
              : mapped;
            return {
              id: e.id,
              source: e.sourceId,
              target: e.targetId,
              label: edgeLabel(canonicalType),
              type: 'kaiva',
              data: { canonicalType },
            };
          });

          // Restore clusters and dismissed nodes from persisted state
          if (response.canvasState.clusters?.length) {
            setInitialClusters(response.canvasState.clusters);
          }
          if (response.canvasState.dismissedNodes?.length) {
            setDismissedNodes(response.canvasState.dismissedNodes);
          }
        }

        // Add preloaded knowledge entities (existing knowledge)
        if (response.preloadedEntities?.length) {
          const existingIds = new Set(initialNodes.map((n) => n.id));
          const newPreloaded = response.preloadedEntities
            .filter((e) => !existingIds.has(e.id))
            .map((e, i) => toReactFlowNode(e, initialNodes.length + i));
          initialNodes = [...initialNodes, ...newPreloaded];
        }

        // Update edge counts
        initialNodes = updateEdgeCounts(initialNodes, initialEdges);

        // Always apply dagre layout for a clean arrangement
        if (initialNodes.length > 0) {
          initialNodes = applyDagreLayout(initialNodes, initialEdges);
          initialEdges = applyEdgeAnchors(initialEdges, initialNodes);
        }

        setNodes(initialNodes);
        setEdges(initialEdges);
        setLayoutVersion((v) => v + 1);
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : 'Failed to load canvas';
        setCanvasError(msg);
        console.warn('[ProcessCanvas] Failed to load canvas context:', err);
      } finally {
        if (!cancelled) setIsLoadingCanvas(false);
      }
    }

    void loadCanvas();
    return () => {
      cancelled = true;
    };
  }, [sessionId, setNodes, setEdges, toReactFlowNode, updateEdgeCounts]);

  // ── Apply a canvas delta from a chat message response ────────────────────

  const applyDelta = useCallback(
    (delta: CanvasDelta) => {
      setNodes((prevNodes) => {
        let updatedNodes = [...prevNodes];

        for (const nodeUpdate of delta.nodes) {
          // Match by ID first, then fall back to name+type match.
          let existingIndex = updatedNodes.findIndex(
            (n) => n.id === nodeUpdate.id,
          );
          if (existingIndex < 0) {
            const nameLower = nodeUpdate.name.toLowerCase();
            const displayType = mapToDisplayType(nodeUpdate.type);
            existingIndex = updatedNodes.findIndex(
              (n) => n.data.label.toLowerCase() === nameLower
                && n.data.canvasNodeType === displayType,
            );
          }

          if (existingIndex >= 0) {
            // Update or activate existing node
            const existing = updatedNodes[existingIndex];
            updatedNodes[existingIndex] = {
              ...existing,
              data: {
                ...existing.data,
                label: nodeUpdate.name,
                description: nodeUpdate.description ?? existing.data.description,
                sourceType: nodeUpdate.action === 'conflict' ? 'conflicting' : existing.data.sourceType,
                conflictNew: nodeUpdate.conflictNew ?? existing.data.conflictNew,
                conflictExisting: nodeUpdate.conflictExisting ?? existing.data.conflictExisting,
                section: nodeUpdate.section ?? existing.data.section,
                stepNumber: nodeUpdate.stepNumber ?? existing.data.stepNumber,
              },
            };
          } else {
            // Add new node as staged (provisional)
            updatedNodes.push(
              toReactFlowNode(nodeUpdate, updatedNodes.length, true),
            );
          }
        }

        return updatedNodes;
      });

      setEdges((prevEdges) => {
        const updatedEdges = [...prevEdges];

        for (const edgeUpdate of delta.edges) {
          const mappedType = mapEdgeType(edgeUpdate.type);
          const existingIndex = updatedEdges.findIndex(
            (e) => e.id === edgeUpdate.id,
          );

          if (existingIndex >= 0) {
            updatedEdges[existingIndex] = {
              ...updatedEdges[existingIndex],
              source: edgeUpdate.sourceId,
              target: edgeUpdate.targetId,
              label: edgeLabel(mappedType),
              data: { ...updatedEdges[existingIndex].data, canonicalType: mappedType },
            };
          } else {
            updatedEdges.push({
              id: edgeUpdate.id,
              source: edgeUpdate.sourceId,
              target: edgeUpdate.targetId,
              label: edgeLabel(mappedType),
              type: 'kaiva',
              data: { canonicalType: mappedType },
            });
          }
        }

        return updatedEdges;
      });

      // Always run dagre layout after every delta, then bump version for fitView
      queueMicrotask(() => {
        let laidOutSnapshot: CanvasFlowNode[] | null = null;
        setNodes((currentNodes) => {
          const currentEdges = edgesRef.current;
          const withCounts = updateEdgeCounts(currentNodes, currentEdges);
          const laidOut = applyDagreLayout(withCounts, currentEdges);
          laidOutSnapshot = laidOut;
          return laidOut;
        });
        setEdges((prevEdges) =>
          laidOutSnapshot ? applyEdgeAnchors(prevEdges, laidOutSnapshot) : prevEdges,
        );
        setLayoutVersion((v) => v + 1);
      });
    },
    [setNodes, setEdges, toReactFlowNode, updateEdgeCounts],
  );

  // ── Track C.3 — Timeline scrubber replay ─────────────────────────────────
  // Rebuild the canvas state for any prior turn by replaying CanvasEvents up
  // to that turnIndex. `applyDelta` runs inside an isReplaying flag so it
  // doesn't push undo entries. `replayToLatest` re-fetches the live snapshot
  // so we don't drift from server state if anything mutated during replay.

  const replayToIndex = useCallback(
    (idx: number) => {
      const events = eventsRef.current;
      if (events.length === 0) return; // No event log → nothing to replay against

      isReplayingRef.current = true;
      setIsReplaying(true);

      // Reset to empty seed; clusters are preserved (they don't event-source).
      setNodes([]);
      setEdges([]);

      const targetIdx = Math.max(0, Math.min(idx, events.length - 1));
      const slice = events.filter((e) => e.turnIndex <= targetIdx);
      for (const event of slice) {
        applyDelta(event.delta as unknown as CanvasDelta);
      }
    },
    [applyDelta, setNodes, setEdges],
  );

  const replayToLatest = useCallback(async () => {
    isReplayingRef.current = true;
    try {
      // Refresh from authoritative server state — picks up any new turns
      // applied while we were rewound.
      const [refreshed, refreshedEvents] = await Promise.all([
        getCanvasContext(sessionId),
        listCanvasEvents(sessionId).catch(() => eventsRef.current),
      ]);
      eventsRef.current = refreshedEvents;

      const liveNodes: CanvasFlowNode[] = (refreshed.canvasState?.nodes ?? []).map((n, i): CanvasFlowNode => {
        const displayType = mapToDisplayType(n.type);
        const subType = n.subType ?? deriveSubType(n.type);
        return {
          id: n.id,
          type: NODE_TYPE_MAP[displayType] ?? 'step',
          position: n.position ?? { x: i * 220, y: 0 },
          data: {
            label: n.name,
            description: n.description,
            sourceType: n.sourceType,
            canvasNodeType: displayType,
            subType,
            section: n.section,
            stepNumber: n.stepNumber,
            edgeCount: n.edgeCount ?? 0,
            conflictNew: n.conflictNew,
            conflictExisting: n.conflictExisting,
            sourceName: n.sourceName,
            sourceDate: n.sourceDate,
            staged: n.staged ?? false,
            additionalContext: n.additionalContext,
          },
        };
      });

      const liveEdges: Edge[] = (refreshed.canvasState?.edges ?? []).map((e): Edge => {
        const mapped = mapEdgeType(e.type);
        const canonicalType = mapped === 'RELATES_TO' && e.type !== 'RELATES_TO'
          ? inferCanonicalType(e.type)
          : mapped;
        return {
          id: e.id,
          source: e.sourceId,
          target: e.targetId,
          label: edgeLabel(canonicalType),
          type: 'kaiva',
          data: { canonicalType },
        };
      });

      const withCounts = updateEdgeCounts(liveNodes, liveEdges);
      const laidOut = liveNodes.length > 0 ? applyDagreLayout(withCounts, liveEdges) : withCounts;
      const anchoredEdges = liveNodes.length > 0 ? applyEdgeAnchors(liveEdges, laidOut) : liveEdges;
      setNodes(laidOut);
      setEdges(anchoredEdges);
      setLayoutVersion((v) => v + 1);
    } catch (err) {
      console.warn('[ProcessCanvas] replayToLatest refresh failed:', err);
    } finally {
      isReplayingRef.current = false;
      setIsReplaying(false);
    }
  }, [sessionId, setNodes, setEdges, updateEdgeCounts]);

  // ── Staged node actions ──────────────────────────────────────────────────

  // Snapshot helper used by every undoable action.
  const snapshotState = useCallback(() => {
    const prevNodes = nodesRef.current;
    const prevEdges = edgesRef.current;
    return () => {
      setNodes(prevNodes);
      setEdges(prevEdges);
    };
  }, [setNodes, setEdges]);

  const newUndoId = () => `undo-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

  const confirmNode = useCallback(
    (nodeId: string) => {
      const node = nodesRef.current.find((n) => n.id === nodeId);
      const restore = snapshotState();

      setNodes((prev) =>
        prev.map((n) =>
          n.id === nodeId
            ? { ...n, data: { ...n.data, staged: false } }
            : n,
        ),
      );

      pushUndo({
        id: newUndoId(),
        label: `Added "${node?.data.label ?? 'node'}"`,
        restore,
      });
    },
    [setNodes, snapshotState, pushUndo],
  );

  const dismissNode = useCallback(
    (nodeId: string) => {
      const dismissed = nodesRef.current.find((n) => n.id === nodeId);
      const restoreState = snapshotState();
      const restore = () => {
        restoreState();
        setDismissedNodes((d) => d.filter((x) => x.id !== nodeId));
      };

      // Track the dismissed node before removing it
      if (dismissed) {
        setDismissedNodes((d) => [
          ...d,
          {
            id: nodeId,
            name: dismissed.data.label,
            type: dismissed.data.canvasNodeType,
            dismissedAt: new Date().toISOString(),
          },
        ]);
      }
      setNodes((prev) => prev.filter((n) => n.id !== nodeId));
      setEdges((prev) =>
        prev.filter((e) => e.source !== nodeId && e.target !== nodeId),
      );
      // Re-run layout after removal but DON'T bump layoutVersion —
      // the StagedNodeReviewer handles focus navigation for remaining staged nodes.
      // Bumping layoutVersion would trigger the auto-zoom effect which fights the reviewer.
      queueMicrotask(() => {
        let laidOutSnapshot: CanvasFlowNode[] | null = null;
        setNodes((currentNodes) => {
          const currentEdges = edgesRef.current;
          const withCounts = updateEdgeCounts(currentNodes, currentEdges);
          const laidOut = applyDagreLayout(withCounts, currentEdges);
          laidOutSnapshot = laidOut;
          return laidOut;
        });
        setEdges((prevEdges) =>
          laidOutSnapshot ? applyEdgeAnchors(prevEdges, laidOutSnapshot) : prevEdges,
        );
      });

      pushUndo({
        id: newUndoId(),
        label: `Skipped "${dismissed?.data.label ?? 'node'}"`,
        restore,
      });
    },
    [setNodes, setEdges, updateEdgeCounts, snapshotState, pushUndo],
  );

  const addNodeContext = useCallback(
    (nodeId: string, text: string, kind?: 'detail' | 'exception' | 'correction') => {
      const entry = { text, timestamp: new Date().toISOString(), ...(kind ? { kind } : {}) };
      setNodes((prev) =>
        prev.map((n) =>
          n.id === nodeId
            ? {
                ...n,
                data: {
                  ...n.data,
                  additionalContext: [...(n.data.additionalContext ?? []), entry],
                },
              }
            : n,
        ),
      );
    },
    [setNodes],
  );

  // ── Manual relayout (Phase 8 polish) ───────────────────────────────────────

  /**
   * Re-runs dagre on the current node + edge set and bumps `layoutVersion`
   * so the auto-zoom effect re-fits. Exposed via the toolbar's "Re-arrange"
   * button so users can untangle the canvas after lots of additions.
   */
  const relayout = useCallback(() => {
    let laidOutSnapshot: CanvasFlowNode[] | null = null;
    setNodes((current) => {
      if (current.length === 0) return current;
      const currentEdges = edgesRef.current;
      const withCounts = updateEdgeCounts(current, currentEdges);
      const laidOut = applyDagreLayout(withCounts, currentEdges);
      laidOutSnapshot = laidOut;
      return laidOut;
    });
    setEdges((prevEdges) =>
      laidOutSnapshot ? applyEdgeAnchors(prevEdges, laidOutSnapshot) : prevEdges,
    );
    setLayoutVersion((v) => v + 1);
  }, [setNodes, setEdges, updateEdgeCounts]);

  // ── Batch staged actions (Phase 5.1) ───────────────────────────────────────

  const acceptAllStaged = useCallback(() => {
    const stagedCount = nodesRef.current.filter((n) => n.data.staged).length;
    if (stagedCount === 0) return;
    const restore = snapshotState();

    setNodes((prev) =>
      prev.map((n) =>
        n.data.staged ? { ...n, data: { ...n.data, staged: false } } : n,
      ),
    );

    pushUndo({
      id: newUndoId(),
      label: `Accepted ${stagedCount} proposal${stagedCount > 1 ? 's' : ''}`,
      restore,
    });
  }, [setNodes, snapshotState, pushUndo]);

  const skipAllStaged = useCallback(() => {
    const stagedIds = nodesRef.current.filter((n) => n.data.staged).map((n) => n.id);
    if (stagedIds.length === 0) return;
    const stagedSet = new Set(stagedIds);
    const restoreState = snapshotState();
    const restore = () => {
      restoreState();
      setDismissedNodes((d) => d.filter((x) => !stagedSet.has(x.id)));
    };

    // Track dismissed entries for coordinator awareness
    const now = new Date().toISOString();
    const dismissed = nodesRef.current
      .filter((n) => stagedSet.has(n.id))
      .map((n) => ({
        id: n.id,
        name: n.data.label,
        type: n.data.canvasNodeType,
        dismissedAt: now,
      }));
    setDismissedNodes((d) => [...d, ...dismissed]);

    setNodes((prev) => prev.filter((n) => !stagedSet.has(n.id)));
    setEdges((prev) =>
      prev.filter((e) => !stagedSet.has(e.source) && !stagedSet.has(e.target)),
    );

    // Re-layout once nodes + edges have settled
    queueMicrotask(() => {
      let laidOutSnapshot: CanvasFlowNode[] | null = null;
      setNodes((current) => {
        const currentEdges = edgesRef.current;
        const withCounts = updateEdgeCounts(current, currentEdges);
        const laidOut = applyDagreLayout(withCounts, currentEdges);
        laidOutSnapshot = laidOut;
        return laidOut;
      });
      setEdges((prevEdges) =>
        laidOutSnapshot ? applyEdgeAnchors(prevEdges, laidOutSnapshot) : prevEdges,
      );
    });

    pushUndo({
      id: newUndoId(),
      label: `Skipped ${stagedIds.length} proposal${stagedIds.length > 1 ? 's' : ''}`,
      restore,
    });
  }, [setNodes, setEdges, updateEdgeCounts, snapshotState, pushUndo]);

  return {
    nodes,
    edges,
    onNodesChange: onNodesChange as OnNodesChange<CanvasFlowNode>,
    onEdgesChange,
    applyDelta,
    confirmNode,
    dismissNode,
    acceptAllStaged,
    skipAllStaged,
    addNodeContext,
    undoAction,
    undoLast,
    relayout,
    isLoadingCanvas,
    canvasError,
    layoutVersion,
    initialClusters,
    dismissedNodes,
    isReplaying,
    replayToIndex,
    replayToLatest,
  };
}
